import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faTimes, 
  faCheck,
  faTrash,
  faEye,
  faHistory,
  faComments,
  faBook
} from '@fortawesome/free-solid-svg-icons';
import { OutcomeTaskService, sharedApiClient } from '../fetcher';
import { CustomDropdown, useBugReportContext } from '@medprompt/shared';
import { useNotifications, useAuth, PRESENCE_SCOPE, PATIENT_DIARY_LAYOUT_SIDEBAR } from '@medprompt/shared';
import { TaskTypeEnum } from '../enums/taskTypeEnum';
import { UrgencyEnum, getUrgency } from '../enums/urgencyEnum';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import PatientDiaryPanel from '../detail/PatientDiaryPanel';
import useTaskPatientDiary from './useTaskPatientDiary';
import ConsultationPreviewModal from '../components/ConsultationPreviewModal';
import ActivityModal, { EntityType } from '../detail/tabs/ActivityModal';
import CommentsPanel from '../components/CommentsPanel';
import ViewerCountBadge from '../components/ViewerCountBadge';
import { StatusBadge } from '@medprompt/shared';
import { createAssigneeOptions, getAssigneeIcon, outOfOfficeForAssignee, OutOfOfficeMark } from '@medprompt/shared';
import './OutcomeTaskView.css';
import '@medprompt/shared/src/common/modals.css';
import '@medprompt/shared/src/common/dropdowns.css';
import DeleteTaskModal from './DeleteTaskModal';

// Utility function to format NHS ID with spacing (XXX XXX XXXX)
const formatNhsId = (nhsId) => {
  if (!nhsId || nhsId.length !== 10) return nhsId;
  
  try {
    return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6)}`;
  } catch (error) {
    console.error('Error formatting NHS ID:', error);
    return nhsId;
  }
};

// Utility function to format DD/MM/YYYY to readable format
const formatDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  try {
    const [day, month, year] = dateOfBirth.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day);
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthName = monthNames[birthDate.getMonth()];
    const dayNum = birthDate.getDate();
    const yearNum = birthDate.getFullYear();
    
    return `${monthName} ${dayNum}, ${yearNum}`;
  } catch (error) {
    console.error('Error formatting date of birth:', error);
    return dateOfBirth;
  }
};

// Utility function to calculate age from DD/MM/YYYY date string
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  try {
    const [day, month, year] = dateOfBirth.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 0 ? `${age}y` : null;
  } catch (error) {
    console.error('Error calculating age:', error);
    return null;
  }
};

// Task Edit/View Modal component
const TaskModal = ({ isOpen, onClose, task, isReadOnly, onSave, onMarkComplete, onDelete, loading, activeUsers, teams, getAssigneeName, getViewerCount, getViewers }) => {
  const navigate = useNavigate();
  const { addNotification, setViewing } = useNotifications();
  const { user } = useAuth();
  const { setBugContext, clearBugContext } = useBugReportContext();
  const [editedText, setEditedText] = useState('');
  const [editedTaskType, setEditedTaskType] = useState('');
  const [editedAssigneeId, setEditedAssigneeId] = useState('');
  const [editedIsUrgent, setEditedIsUrgent] = useState(false);
  const [taskTypeDropdownOpen, setTaskTypeDropdownOpen] = useState(false);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const [urgencyDropdownOpen, setUrgencyDropdownOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState(null);
  const [isPatientDiaryVisible, setIsPatientDiaryVisible] = useState(false);
  const [showConsultationPreview, setShowConsultationPreview] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCommentsVisible, setIsCommentsVisible] = useState(false);
  const [localTask, setLocalTask] = useState(task);
  const descriptionTextareaRef = useRef(null);
  const outcomeTaskManager = new OutcomeTaskService(sharedApiClient);
  const patientNhsId = task?.patient?.nhsId;
  const patientId = task?.patient?.patientId;
  const hasPatient = Boolean(patientNhsId && patientId);
  const patientDiary = useTaskPatientDiary({
    enabled: isOpen && isPatientDiaryVisible && hasPatient,
    nhsId: patientNhsId,
    patientId,
  });
  const diaryDoc = useMemo(() => ({
    documentId: task?.documentId,
    consultationId: task?.consultationId,
  }), [task?.documentId, task?.consultationId]);

  useEffect(() => {
    if (task) {
      setEditedText(task.text || '');
      setEditedTaskType(task.taskType || '');
      setEditedAssigneeId(task.assigneeId || '*');
      setEditedIsUrgent(task.isUrgent || false);
      setLocalTask(task);
    }
  }, [task]);

  useEffect(() => {
    if (isOpen && task) {
      const taskTypeDisplay = TaskTypeEnum[task.taskType]?.displayName || task.taskType;
      setBugContext(`Task ID: ${task.taskId}\nTask Type: ${taskTypeDisplay}\n\n`);
    }
    return () => clearBugContext();
  }, [isOpen, task, setBugContext, clearBugContext]);

  // Presence now travels over the update socket instead of a POST every 15 seconds
  useEffect(() => {
    if (!isOpen || !task?.taskId) return;

    setViewing({ scope: PRESENCE_SCOPE.tasks, itemId: task.taskId });
    return () => setViewing(null);
  }, [isOpen, task?.taskId, setViewing]);

  useEffect(() => {
    if (isEditingDescription && descriptionTextareaRef.current) {
      descriptionTextareaRef.current.focus();
    }
  }, [isEditingDescription]);

  useEffect(() => {
    if (!isOpen) {
      setIsPatientDiaryVisible(false);
      setPreviewDocumentId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setIsPatientDiaryVisible(false);
    setPreviewDocumentId(null);
  }, [task?.taskId]);

  const hasChanges = useMemo(() => {
    if (!task) return false;
    return (
      editedText !== (task.text || '') ||
      editedTaskType !== (task.taskType || '') ||
      editedAssigneeId !== (task.assigneeId || '*') ||
      editedIsUrgent !== (task.isUrgent || false)
    );
  }, [task, editedText, editedTaskType, editedAssigneeId, editedIsUrgent]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-select')) {
        setTaskTypeDropdownOpen(false);
        setAssigneeDropdownOpen(false);
        setUrgencyDropdownOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleDescriptionClick = () => {
    if (!isReadOnly) {
      setIsEditingDescription(true);
    }
  };

  const handleDescriptionBlur = () => {
    setIsEditingDescription(false);
    if (editedText !== (task.text || '') && editedText.trim()) {
      onSave({
        taskId: task.taskId,
        text: editedText
      });
    }
  };

  const handleDescriptionKeyDown = (e) => {
    if (e.key === 'Escape') {
      setEditedText(task.text || '');
      setIsEditingDescription(false);
    }
  };

  const handleTaskTypeChange = (taskType) => {
    setTaskTypeDropdownOpen(false);
    setEditedTaskType(taskType.id);
    setLocalTask(prev => ({ ...prev, taskType: taskType.id }));
    onSave({
      taskId: task.taskId,
      taskType: taskType.id
    });
  };

  const handleAssigneeChange = (assigneeOption) => {
    const newAssigneeId = assigneeOption.userId === null ? '*' : assigneeOption.userId;
    setAssigneeDropdownOpen(false);
    setEditedAssigneeId(newAssigneeId);
    setLocalTask(prev => ({ ...prev, assigneeId: newAssigneeId }));
    onSave({
      taskId: task.taskId,
      assigneeId: newAssigneeId
    });
  };

  const handleUrgencyChange = (urgencyOption) => {
    const isUrgent = urgencyOption.id;
    setUrgencyDropdownOpen(false);
    setEditedIsUrgent(isUrgent);
    setLocalTask(prev => ({ ...prev, isUrgent }));
    const urgencyUpdate = isUrgent ? { isUrgent: true } : { isNotUrgent: true };
    onSave({
      taskId: task.taskId,
      ...urgencyUpdate
    });
  };

  const handleMarkComplete = () => {
    onMarkComplete({
      taskId: task.taskId
    });
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    setDeleteModalOpen(false);
    onDelete(task.taskId);
  };

  const handleAddComment = async (commentText) => {
    if (!commentText.trim() || !task) return;
    
    try {
      await outcomeTaskManager.addTaskComment(task.taskId, commentText);
      
      const newComment = {
        commentId: `temp-${Date.now()}`,
        comment: commentText,
        timestamp: new Date().toISOString(),
        userId: user?.userId,
        name: user?.name
      };
      
      setLocalTask(prevTask => ({
        ...prevTask,
        comments: [...(prevTask.comments || []), newComment]
      }));
      
      addNotification(`task-comment-${task.taskId}`, 'Comment added successfully');
    } catch (error) {
      console.error('Error adding comment to task:', error);
      addNotification(`task-comment-error-${task.taskId}`, 'Failed to add comment', { isError: true });
      throw error;
    }
  };

  if (!isOpen || !task) return null;

  const taskTypeOptions = Object.keys(TaskTypeEnum)
    .filter(typeKey => typeKey !== 'PATIENT_NOTE')
    .map(typeKey => ({ 
      id: typeKey, 
      description: TaskTypeEnum[typeKey].description,
      icon: TaskTypeEnum[typeKey].icon
    }));
  
  const urgencyOptions = [
    { 
      id: true, 
      description: UrgencyEnum.URGENT.description,
      icon: UrgencyEnum.URGENT.icon
    },
    { 
      id: false, 
      description: UrgencyEnum.NORMAL.description,
      icon: UrgencyEnum.NORMAL.icon
    }
  ];
  
  const assigneeOptions = createAssigneeOptions(activeUsers, teams);
  const selectedIcon = getAssigneeIcon(editedAssigneeId === '*' ? null : editedAssigneeId, activeUsers, teams);
  const selectedTaskTypeIcon = TaskTypeEnum[editedTaskType]?.icon;
  const currentUrgency = getUrgency(editedIsUrgent);

  return (
    <div className="modal-overlay">
      <div className={`modal-container task-modal-wide${isPatientDiaryVisible && hasPatient ? ' with-patient-diary' : ''}`}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <StatusBadge
              label={currentUrgency.displayName}
              colorFamily={currentUrgency.colorFamily || 'gray'}
              size="normal"
            />
            <h2>{isReadOnly ? 'View Task' : 'Edit Task'}</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-content task-modal-content-horizontal">
          <div className="task-modal-left-panel">
            <div className="patient-card">
              <div className="patient-card-header">
                <h3>{task.patient?.name || 'Unknown Patient'}</h3>
              </div>
              <div className="patient-card-details">
                <div className="detail-row">
                  <span className="detail-label">NHS Number:</span>
                  <span className="detail-value">
                    {formatNhsId(task.patient?.nhsId) || <em>not found</em>}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date of birth:</span>
                  <span className="detail-value">
                    {formatDateOfBirth(task.patient?.dateOfBirth) || <em>not found</em>}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Age:</span>
                  <span className="detail-value">
                    {calculateAge(task.patient?.dateOfBirth) || <em>not found</em>}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Sex:</span>
                  <span className="detail-value">
                    {task.patient?.sex || <em>not found</em>}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">
                    {task.patient?.address || <em>not found</em>}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">
                    {task.patient?.phoneNumber || <em>not found</em>}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">
                    {task.patient?.email || <em>not found</em>}
                  </span>
                </div>
              </div>
            </div>

            <div className="task-description-container">
              <label className="task-description-label">Task Description:</label>
              {isEditingDescription && !isReadOnly ? (
                <textarea
                  ref={descriptionTextareaRef}
                  className="task-description-textarea"
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  onBlur={handleDescriptionBlur}
                  onKeyDown={handleDescriptionKeyDown}
                  rows={6}
                />
              ) : (
                <div 
                  className={`task-description-text ${!isReadOnly ? 'task-description-editable' : ''}`}
                  onClick={handleDescriptionClick}
                  title={!isReadOnly ? "Click to edit" : ""}
                >
                  {editedText || <em style={{ color: '#999' }}>No description</em>}
                </div>
              )}
            </div>
          </div>

          <div className="task-modal-right-panel">
            <div className="task-modal-field">
              <label className="task-modal-field-label">Urgency:</label>
              {isReadOnly ? (
                <StatusBadge
                  label={currentUrgency.description}
                  colorFamily={currentUrgency.colorFamily || 'gray'}
                  size="normal"
                />
              ) : (
                <CustomDropdown
                  selected={currentUrgency.description}
                  selectedIcon={currentUrgency.icon}
                  options={urgencyOptions}
                  isOpen={urgencyDropdownOpen}
                  toggleDropdown={() => setUrgencyDropdownOpen(!urgencyDropdownOpen)}
                  onSelect={(option) => handleUrgencyChange(option)}
                  keyExtractor={(option) => option.id}
                  selectedStyle={{ 
                    backgroundColor: currentUrgency.color,
                    color: '#000',
                    fontWeight: 'bold',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}
                />
              )}
            </div>

            <div className="task-modal-field">
              <label className="task-modal-field-label">Task Type:</label>
              {isReadOnly ? (
                <div className="task-modal-field-value">
                  {TaskTypeEnum[task.taskType]?.description || task.taskType}
                </div>
              ) : (
                <CustomDropdown
                  selected={TaskTypeEnum[editedTaskType]?.description || 'Select Task Type'}
                  selectedIcon={selectedTaskTypeIcon}
                  options={taskTypeOptions}
                  isOpen={taskTypeDropdownOpen}
                  toggleDropdown={() => setTaskTypeDropdownOpen(!taskTypeDropdownOpen)}
                  onSelect={(option) => handleTaskTypeChange(option)}
                  keyExtractor={(option) => option.id}
                />
              )}
            </div>

            <div className="task-modal-field">
              <label className="task-modal-field-label">Assignee:</label>
              {isReadOnly ? (
                <div className="task-modal-field-value assignee-with-availability">
                  {getAssigneeName(task.assigneeId) || 'Unassigned'}
                  <OutOfOfficeMark outOfOffice={outOfOfficeForAssignee(task.assigneeId, activeUsers)} />
                </div>
              ) : (
                <CustomDropdown
                  searchable
                  searchPlaceholder="Search assignees..."
                  selected={getAssigneeName(editedAssigneeId) || 'Select Assignee'}
                  selectedIcon={selectedIcon}
                  selectedOutOfOffice={outOfOfficeForAssignee(editedAssigneeId === '*' ? null : editedAssigneeId, activeUsers)}
                  options={assigneeOptions}
                  isOpen={assigneeDropdownOpen}
                  toggleDropdown={() => setAssigneeDropdownOpen(!assigneeDropdownOpen)}
                  onSelect={(option) => handleAssigneeChange(option)}
                  keyExtractor={(option) => option.id}
                />
              )}
            </div>

            {task.documentId && (
              <button
                className="task-action-button task-preview-document-button"
                onClick={() => setPreviewDocumentId(task.documentId)}
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Preview Document</span>
              </button>
            )}

            {task.consultationId && (
              <button
                className="task-action-button task-preview-document-button"
                onClick={() => setShowConsultationPreview(true)}
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Preview Consultation</span>
              </button>
            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
              {getViewerCount && getViewers && (
                <ViewerCountBadge
                  itemId={task.taskId}
                  getViewerCount={getViewerCount}
                  getViewers={getViewers}
                  activeUsers={activeUsers}
                  user={user}
                  itemType="task"
                />
              )}
              <button 
                className={`task-action-button task-comments-button ${isCommentsVisible ? 'active' : ''}`}
                onClick={() => setIsCommentsVisible(!isCommentsVisible)}
                style={{ flex: '1' }}
              >
                <FontAwesomeIcon icon={faComments} />
                <span>Comments</span>
                {localTask?.comments && localTask.comments.length > 0 && (
                  <span className="comment-count-badge">{localTask.comments.length}</span>
                )}
              </button>
            </div>

            <div className="task-modal-button-row">
              <button
                className="task-action-button task-preview-document-button"
                onClick={() => setShowActivityModal(true)}
              >
                <FontAwesomeIcon icon={faHistory} />
                <span>Audit Trail</span>
              </button>
              <button
                className={`task-action-button task-preview-document-button task-patient-diary-button${isPatientDiaryVisible ? ' active' : ''}`}
                onClick={() => setIsPatientDiaryVisible((visible) => !visible)}
                disabled={!hasPatient}
                title={hasPatient ? 'Toggle patient diary' : 'No patient on this task'}
              >
                <FontAwesomeIcon icon={faBook} />
                <span>Patient Diary</span>
              </button>
            </div>

            {!task.isComplete && !isReadOnly && (
              <button 
                className="task-action-button task-mark-complete-button"
                onClick={handleMarkComplete}
                disabled={loading}
              >
                {loading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Mark Complete</span>
                  </>
                )}
              </button>
            )}

            <button 
              className="task-action-button task-delete-button"
              onClick={handleDeleteClick}
              disabled={loading}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faTrash} />
                  <span>Delete Task</span>
                </>
              )}
            </button>
          </div>

          {isPatientDiaryVisible && hasPatient && (
            <div className="task-modal-patient-diary">
              <PatientDiaryPanel
                patientData={task.patient}
                patientRecordData={patientDiary.patientRecordData}
                patientRecordDataError={patientDiary.patientRecordDataError}
                doc={diaryDoc}
                onDocumentClick={setPreviewDocumentId}
                layoutMode={PATIENT_DIARY_LAYOUT_SIDEBAR}
                canEdit={false}
                onRefreshPatientCodes={patientDiary.handleRefreshPatientCodes}
                isRefreshingPatientCodes={patientDiary.refreshingPatientCodes}
              />
            </div>
          )}
        </div>
      </div>

      {previewDocumentId && (
        <DocumentPreviewModal
          isOpen={Boolean(previewDocumentId)}
          onClose={() => setPreviewDocumentId(null)}
          documentId={previewDocumentId}
          activeUsers={activeUsers}
          teams={teams}
          taskId={previewDocumentId === task.documentId ? task.taskId : undefined}
        />
      )}

      {showConsultationPreview && task.consultationId && (
        <ConsultationPreviewModal
          isOpen={showConsultationPreview}
          onClose={() => setShowConsultationPreview(false)}
          consultationId={task.consultationId}
        />
      )}

      {showActivityModal && (
        <ActivityModal
          isOpen={showActivityModal}
          onClose={() => setShowActivityModal(false)}
          entityType={EntityType.TASK}
          entityId={task.taskId}
          entity={task}
          activeUsers={activeUsers}
          teams={teams}
        />
      )}

      <DeleteTaskModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        task={task}
        loading={loading}
      />

      <CommentsPanel
        comments={localTask?.comments || []}
        activeUsers={activeUsers}
        currentUser={user}
        isVisible={isCommentsVisible}
        onClose={() => setIsCommentsVisible(false)}
        onAddComment={handleAddComment}
      />
    </div>
  );
};

export default TaskModal;
